import { useTranslation } from "react-i18next";
import { ForkKnifeIcon } from "./ForkKnifeIcon";
import { useLunchMenuColoring } from "./LunchMenuColoringContext";

// Цветът на менюто по мястото му (`lib/lunchMenuColors.ts`) — същият ред като в
// справката „По артикули": оранжево (досегашното), синьо, зелено, сиво.
const LUNCH_MENU_TAG_CLASSES = [
  "border-pos-accent/35 bg-pos-marked text-pos-accent-ink",
  "border-info/35 bg-info-tint text-info-deep",
  "border-ok/35 bg-ok-tint text-ok-deep",
  "border-fill-3 bg-fill text-body",
];
// Оцветяването е „Без" (`LunchMenuColoringContext`) — неутрален етикет.
const UNCOLORED_TAG_CLASSES = "border-pos-ctl bg-pos-card text-pos-soft";

/**
 * Етикет „Обедно" до името на продукт от обедно меню — в каталога (плочки,
 * списък, търсене), в кошницата, в историята на масата и на мониторите.
 * Каталогът подава името на менюто (`label`) и цвета му (`colorIndex`); без тях
 * — общото „Дневно" в оранжево. При оцветяване „Без" — неутрален. Дълго име се
 * реже с „…" (цялото — в подсказката).
 *
 * При изчерпан дневен лимит — „Изчерпано" (броят „N бр" е отделно, до
 * мерната единица — `LunchCount`).
 *
 * `inline` — етикетът седи ВЪТРЕ в текста на името (редът може да се пренася,
 * затова не е flex): `align-middle` слага средата му на средата на малките
 * букви, а -1px я вдига към средата на реда с главна буква — така средата на
 * етикета съвпада със средата на текста при всеки размер на шрифта. Без
 * `inline` родителят е flex с items-center и центрира сам.
 */
export const LunchTag = ({
  className = "",
  inline = false,
  soldOut = false,
  label,
  colorIndex = 0,
}: {
  className?: string;
  inline?: boolean;
  /** Порциите за деня са свършили — „Изчерпано" в червено */
  soldOut?: boolean;
  /** Името на менюто; без него — „Дневно". */
  label?: string;
  colorIndex?: number;
}) => {
  const { t } = useTranslation();
  const colored = useLunchMenuColoring();
  const text = soldOut ? t("lunchMenu.tagSoldOut") : label || t("lunchMenu.tag");
  return (
    <span
      title={text}
      className={`inline-flex h-5 max-w-[9rem] shrink-0 items-center gap-1 rounded-md border px-1.5 text-[11px] font-semibold leading-none ${
        soldOut
          ? "border-pos-danger-brd bg-pos-danger-bg text-pos-danger-ink"
          : colored
            ? LUNCH_MENU_TAG_CLASSES[colorIndex % LUNCH_MENU_TAG_CLASSES.length]
            : UNCOLORED_TAG_CLASSES
      } ${inline ? "align-middle -translate-y-px" : ""} ${className}`}
    >
      {!soldOut && <ForkKnifeIcon className="shrink-0 text-[10px]" />}
      {/* leading-[1.2] — с leading-none „…" реже кирилските у/д/р отдолу */}
      <span className="min-w-0 truncate leading-[1.2]">{text}</span>
    </span>
  );
};

/**
 * „11 бр" — колко порции от обедния продукт остават за днес (минус кошницата);
 * 0 — в червено. Само за продукт с дневен лимит.
 */
export const LunchCount = ({ count, className = "" }: { count: number; className?: string }) => {
  const { t } = useTranslation();
  return (
    <span
      title={t("lunchMenu.remainingTitle", { count })}
      className={`shrink-0 whitespace-nowrap font-pos-mono font-bold ${
        count <= 0 ? "text-pos-danger-ink" : "text-pos-accent-ink"
      } ${className}`}
    >
      {t("lunchMenu.countLeft", { count })}
    </span>
  );
};
